/**
 * GraphQLProductManager - Handles product loading via Shopify Storefront API (GraphQL)
 * 
 * Fetches products with ALL metafields in a single query, eliminating the need for
 * separate metafield API calls. Uses cursor-based pagination to handle large collections.
 */

class GraphQLProductManager {
  constructor() {
    this.cache = new Map(); // Cache full collections to avoid redundant API calls
    this.partialCache = new Map(); // Cache for first-page-only loads (before full load completes)
    this.loadingPromises = new Map(); // Track in-progress background loads
    this.searchCache = new Map(); // Cache full search results keyed by query string
    this.searchPartialCache = new Map(); // Partial search results (first page only)
    this.searchLoadingPromises = new Map(); // Track in-progress background search loads
    this.storefrontToken = window.SHOPIFY_STOREFRONT_TOKEN;
    this.apiVersion = '2024-10'; // Shopify API version (2024-04+ required for productsCount)
    
    if (!this.storefrontToken) {
      console.error('Shopify Storefront API token not found. Please configure it in theme settings.');
    }
  }

  /**
   * Fetch filtered products with client-side filtering
   * @param {string} collectionHandle - The collection handle
   * @param {Object} filters - Filter options
   * @param {string} filters.maker - Vendor/maker filter
   * @param {string} filters.era - Decade/era filter
   * @param {boolean} filters.includeSold - Include sold items
   * @param {string} filters.sort - Sort option
   * @returns {Promise<Array>} Filtered and sorted products
   */
  async fetchFilteredProducts(collectionHandle, filters) {
    let allProducts;

    // Check if we have the full collection cached
    if (this.cache.has(collectionHandle)) {
      allProducts = this.cache.get(collectionHandle);
    } else {
      // Fetch ALL products from collection via GraphQL
      allProducts = await this.fetchCollectionFromGraphQL(collectionHandle);

      // Cache the FULL collection (unfiltered!)
      this.cache.set(collectionHandle, allProducts);
    }

    // CLIENT-SIDE FILTERING (instant after first load)
    const filtered = this.applyClientFilters(allProducts, filters);

    // CLIENT-SIDE SORTING
    const sorted = this.sortProducts(filtered, filters.sort);

    return sorted;
  }

  /**
   * FAST PATH: Fetch only the first page of products with server-side sorting
   * Used when loading a collection with default settings (no URL params)
   * @param {string} collectionHandle - The collection handle
   * @param {number} displayCount - Number of products to return for display (default 12)
   * @returns {Promise<Object>} { products, hasMore, cursor }
   */
  async fetchFirstPageFast(collectionHandle, displayCount = 12) {
    // If we already have full cache, use it
    if (this.cache.has(collectionHandle)) {
      const allProducts = this.cache.get(collectionHandle);
      const available = allProducts.filter(p => p.available);
      // Partition on-hold items to end before slicing for display
      const partitioned = this.partitionOnHoldToEnd(available);
      return {
        products: partitioned.slice(0, displayCount),
        hasMore: partitioned.length > displayCount,
        isFullyCached: true,
        totalAvailable: partitioned.length
      };
    }

    console.log(`Fast path: Fetching first page of ${collectionHandle} with server-side CREATED sort`);

    const query = this.buildCollectionQueryWithSort();
    const variables = {
      handle: collectionHandle,
      first: 250, // Fetch 250 to ensure we have enough after filtering sold items
      after: null,
      sortKey: 'CREATED',
      reverse: true // Newest first
    };

    try {
      const data = await this.executeGraphQLQuery(query, variables);
      
      if (!data || !data.collection) {
        console.error(`Collection not found: ${collectionHandle}`);
        return { products: [], hasMore: false, isFullyCached: false };
      }

      const edges = data.collection.products.edges;
      const pageInfo = data.collection.products.pageInfo;

      // Process products
      const products = edges.map(edge => this.processGraphQLProduct(edge.node));
      
      // Filter out sold items (default behavior)
      const availableProducts = products.filter(p => p.available);
      // Partition on-hold items to end before slicing for display
      const partitioned = this.partitionOnHoldToEnd(availableProducts);

      console.log(`Fast path: Got ${products.length} products, ${partitioned.length} available`);

      // Store partial results for potential background loading
      this.partialCache.set(collectionHandle, {
        products: products,
        cursor: pageInfo.endCursor,
        hasNextPage: pageInfo.hasNextPage
      });

      return {
        products: partitioned.slice(0, displayCount),
        hasMore: pageInfo.hasNextPage || partitioned.length > displayCount,
        isFullyCached: false,
        totalAvailable: partitioned.length,
        cursor: pageInfo.endCursor
      };

    } catch (error) {
      console.error('Fast path GraphQL error:', error);
      throw error;
    }
  }

  /**
   * Load remaining products in the background after fast path initial load
   * @param {string} collectionHandle - The collection handle
   * @param {Function} onComplete - Callback when full load is complete
   */
  async loadRemainingInBackground(collectionHandle, onComplete = null) {
    // If already fully cached, nothing to do
    if (this.cache.has(collectionHandle)) {
      console.log(`${collectionHandle} already fully cached`);
      if (onComplete) onComplete();
      return;
    }

    // If already loading, wait for that to complete
    if (this.loadingPromises.has(collectionHandle)) {
      console.log(`${collectionHandle} already loading in background, waiting...`);
      await this.loadingPromises.get(collectionHandle);
      if (onComplete) onComplete();
      return;
    }

    // Get partial cache data
    const partial = this.partialCache.get(collectionHandle);
    if (!partial) {
      console.warn(`No partial cache for ${collectionHandle}, doing full load`);
      await this.fetchCollectionFromGraphQL(collectionHandle);
      if (onComplete) onComplete();
      return;
    }

    // Start background loading
    console.log(`Background loading remaining products for ${collectionHandle}...`);

    const loadPromise = this.continueLoadingFromCursor(collectionHandle, partial);
    this.loadingPromises.set(collectionHandle, loadPromise);

    try {
      await loadPromise;
      console.log(`Background load complete for ${collectionHandle}`);
      if (onComplete) onComplete();
    } finally {
      this.loadingPromises.delete(collectionHandle);
    }
  }

  /**
   * Continue loading products from a cursor position
   * @param {string} collectionHandle - The collection handle
   * @param {Object} partial - Partial cache data with products, cursor, hasNextPage
   */
  async continueLoadingFromCursor(collectionHandle, partial) {
    const allProducts = [...partial.products];
    let cursor = partial.cursor;
    let hasNextPage = partial.hasNextPage;
    const pageSize = 250;

    while (hasNextPage) {
      const query = this.buildCollectionQueryWithSort();
      const variables = {
        handle: collectionHandle,
        first: pageSize,
        after: cursor,
        sortKey: 'CREATED',
        reverse: true
      };

      try {
        const data = await this.executeGraphQLQuery(query, variables);
        
        if (!data || !data.collection) {
          break;
        }

        const edges = data.collection.products.edges;
        const pageInfo = data.collection.products.pageInfo;

        edges.forEach(edge => {
          const processedProduct = this.processGraphQLProduct(edge.node);
          allProducts.push(processedProduct);
        });

        console.log(`Background: Fetched ${edges.length} more products (total: ${allProducts.length})`);

        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;

      } catch (error) {
        console.error('Background load error:', error);
        throw error;
      }
    }

    // Cache the full collection
    this.cache.set(collectionHandle, allProducts);
    // Clear partial cache
    this.partialCache.delete(collectionHandle);

    console.log(`Full collection cached: ${allProducts.length} products`);
  }

  /**
   * Check if a collection is fully loaded
   * @param {string} collectionHandle - The collection handle
   * @returns {boolean}
   */
  isFullyLoaded(collectionHandle) {
    return this.cache.has(collectionHandle);
  }

  /**
   * Check if a collection is currently loading in background
   * @param {string} collectionHandle - The collection handle
   * @returns {boolean}
   */
  isLoadingInBackground(collectionHandle) {
    return this.loadingPromises.has(collectionHandle);
  }

  /**
   * Fetch ALL products from a collection via GraphQL Storefront API
   * @param {string} collectionHandle - The collection handle
   * @returns {Promise<Array>} Array of processed products with metafields
   */
  async fetchCollectionFromGraphQL(collectionHandle) {
    const allProducts = [];
    let hasNextPage = true;
    let cursor = null;
    const pageSize = 250; // GraphQL max per query

    console.log(`Fetching collection via GraphQL: ${collectionHandle}`);

    while (hasNextPage) {
      const query = this.buildCollectionQueryWithSort();
      const variables = {
        handle: collectionHandle,
        first: pageSize,
        after: cursor,
        sortKey: 'CREATED',
        reverse: true
      };

      try {
        const data = await this.executeGraphQLQuery(query, variables);
        
        if (!data || !data.collection) {
          console.error(`Collection not found: ${collectionHandle}`);
          break;
        }

        const edges = data.collection.products.edges;
        const pageInfo = data.collection.products.pageInfo;

        // Process and add products from this page
        edges.forEach(edge => {
          const processedProduct = this.processGraphQLProduct(edge.node);
          allProducts.push(processedProduct);
        });

        console.log(`Fetched ${edges.length} products (total: ${allProducts.length})`);

        // Check if there are more pages
        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;

      } catch (error) {
        console.error('GraphQL query error:', error);
        throw error;
      }
    }

    console.log(`Fetched ${allProducts.length} total products from ${collectionHandle}`);
    return allProducts;
  }

  /**
   * Build the GraphQL query for fetching collection products with metafields
   * @returns {string} GraphQL query
   */
  buildCollectionQuery() {
    return `
      query getCollection($handle: String!, $first: Int!, $after: String) {
        collection(handle: $handle) {
          products(first: $first, after: $after) {
            edges {
              node {
                id
                title
                handle
                vendor
                tags
                variants(first: 1) {
                  edges {
                    node {
                      id
                      sku
                      price {
                        amount
                      }
                      availableForSale
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "onHold"},
                  {namespace: "custom", key: "decade"},
                  {namespace: "custom", key: "year"},
                  {namespace: "custom", key: "justArrived"},
                  {namespace: "custom", key: "promoHeadline"},
                  {namespace: "custom", key: "ShowPrice"}
                ]) {
                  namespace
                  key
                  value
                }
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;
  }

  /**
   * Build the GraphQL query with server-side sorting support
   * @returns {string} GraphQL query
   */
  buildCollectionQueryWithSort() {
    return `
      query getCollection($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
        collection(handle: $handle) {
          products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse) {
            edges {
              node {
                id
                title
                handle
                vendor
                tags
                variants(first: 1) {
                  edges {
                    node {
                      id
                      sku
                      price {
                        amount
                      }
                      availableForSale
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "onHold"},
                  {namespace: "custom", key: "decade"},
                  {namespace: "custom", key: "year"},
                  {namespace: "custom", key: "justArrived"},
                  {namespace: "custom", key: "promoHeadline"},
                  {namespace: "custom", key: "ShowPrice"}
                ]) {
                  namespace
                  key
                  value
                }
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;
  }

  /**
   * Build the GraphQL query for searching products via the Storefront API
   * @returns {string} GraphQL query
   */
  buildSearchQuery() {
    return `
      query searchProducts($query: String!, $first: Int!, $after: String) {
        search(query: $query, first: $first, after: $after, types: [PRODUCT]) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                vendor
                tags
                variants(first: 1) {
                  edges {
                    node {
                      id
                      sku
                      price {
                        amount
                      }
                      availableForSale
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "onHold"},
                  {namespace: "custom", key: "decade"},
                  {namespace: "custom", key: "year"},
                  {namespace: "custom", key: "justArrived"},
                  {namespace: "custom", key: "promoHeadline"},
                  {namespace: "custom", key: "ShowPrice"}
                ]) {
                  namespace
                  key
                  value
                }
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
        }
      }
    `;
  }

  /**
   * FAST PATH: Fetch first page of search results with full product data
   * @param {string} searchQuery - The search query string
   * @param {number} displayCount - Number of products to return for display (default 12)
   * @returns {Promise<Object>} { products, hasMore, isFullyCached, totalAvailable, totalCount }
   */
  async fetchSearchFirstPageFast(searchQuery, displayCount = 12) {
    // Check full cache first
    if (this.searchCache.has(searchQuery)) {
      const allProducts = this.searchCache.get(searchQuery);
      const available = allProducts.filter(p => p.available);
      const sorted = this.sortProducts(available, 'recent');
      return {
        products: sorted.slice(0, displayCount),
        hasMore: sorted.length > displayCount,
        isFullyCached: true,
        totalAvailable: sorted.length
      };
    }

    console.log(`Search fast path: Fetching first page for "${searchQuery}"`);

    const query = this.buildSearchQuery();
    const variables = { query: searchQuery, first: 250, after: null };

    // Check if search term is numeric (likely a SKU lookup)
    const rawTerm = searchQuery.replace(/^title:/, '');
    const isNumeric = /^\d+$/.test(rawTerm);

    try {
      // Run title search and (for numeric terms) a parallel unprefixed SKU search
      const queries = [this.executeGraphQLQuery(query, variables)];
      if (isNumeric) {
        console.log(`Search fast path: Also searching by SKU "${rawTerm}"`);
        queries.push(this.executeGraphQLQuery(query, { query: rawTerm, first: 20, after: null }));
      }
      const [data, skuData] = await Promise.all(queries);

      if (!data || !data.search) {
        console.error('Search query failed');
        return { products: [], hasMore: false, isFullyCached: false, totalAvailable: 0, totalCount: 0 };
      }

      const edges = data.search.edges;
      const pageInfo = data.search.pageInfo;
      const totalCount = data.search.totalCount;

      // Process products using existing processGraphQLProduct()
      let products = edges.map(edge => this.processGraphQLProduct(edge.node));

      // Merge SKU matches from the parallel query (prepended, deduplicated)
      if (skuData?.search?.edges) {
        const skuMatches = skuData.search.edges
          .map(edge => this.processGraphQLProduct(edge.node))
          .filter(p => p.sku === rawTerm || p.sku.startsWith(rawTerm));
        if (skuMatches.length > 0) {
          console.log(`Search fast path: Found ${skuMatches.length} SKU match(es)`);
          const existingIds = new Set(products.map(p => p.id));
          const uniqueSkuMatches = skuMatches.filter(p => !existingIds.has(p.id));
          products = [...uniqueSkuMatches, ...products];
        }
      }

      // Filter sold items, sort by SKU (most recent first)
      const available = products.filter(p => p.available);
      const sorted = this.sortProducts(available, 'recent');

      console.log(`Search fast path: ${products.length} results, ${available.length} available, ${totalCount} total`);

      // Store partial results for background loading
      this.searchPartialCache.set(searchQuery, {
        products: products,
        cursor: pageInfo.endCursor,
        hasNextPage: pageInfo.hasNextPage,
        totalCount: totalCount
      });

      return {
        products: sorted.slice(0, displayCount),
        hasMore: pageInfo.hasNextPage || sorted.length > displayCount,
        isFullyCached: false,
        totalAvailable: sorted.length,
        totalCount: totalCount
      };

    } catch (error) {
      console.error('Search fast path GraphQL error:', error);
      throw error;
    }
  }

  /**
   * Load remaining search results in the background after fast path initial load
   * @param {string} searchQuery - The search query string
   * @param {Function} onComplete - Callback when full load is complete
   */
  async loadRemainingSearchInBackground(searchQuery, onComplete = null) {
    if (this.searchCache.has(searchQuery)) {
      console.log(`Search "${searchQuery}" already fully cached`);
      if (onComplete) onComplete();
      return;
    }

    if (this.searchLoadingPromises.has(searchQuery)) {
      console.log(`Search "${searchQuery}" already loading, waiting...`);
      await this.searchLoadingPromises.get(searchQuery);
      if (onComplete) onComplete();
      return;
    }

    const partial = this.searchPartialCache.get(searchQuery);
    if (!partial) {
      console.warn(`No partial cache for search "${searchQuery}"`);
      if (onComplete) onComplete();
      return;
    }

    if (!partial.hasNextPage) {
      // First page had all results — move to full cache
      this.searchCache.set(searchQuery, partial.products);
      this.searchPartialCache.delete(searchQuery);
      if (onComplete) onComplete();
      return;
    }

    console.log(`Background loading remaining search results for "${searchQuery}"...`);

    const loadPromise = this.continueSearchLoadingFromCursor(searchQuery, partial);
    this.searchLoadingPromises.set(searchQuery, loadPromise);

    try {
      await loadPromise;
      console.log(`Search background load complete for "${searchQuery}"`);
      if (onComplete) onComplete();
    } finally {
      this.searchLoadingPromises.delete(searchQuery);
    }
  }

  /**
   * Continue loading search results from a cursor position
   * @param {string} searchQuery - The search query string
   * @param {Object} partial - Partial cache data with products, cursor, hasNextPage
   */
  async continueSearchLoadingFromCursor(searchQuery, partial) {
    const allProducts = [...partial.products];
    let cursor = partial.cursor;
    let hasNextPage = partial.hasNextPage;

    while (hasNextPage) {
      const query = this.buildSearchQuery();
      const variables = { query: searchQuery, first: 250, after: cursor };

      try {
        const data = await this.executeGraphQLQuery(query, variables);

        if (!data || !data.search) break;

        const edges = data.search.edges;
        const pageInfo = data.search.pageInfo;

        edges.forEach(edge => {
          allProducts.push(this.processGraphQLProduct(edge.node));
        });

        console.log(`Search background: +${edges.length} products (total: ${allProducts.length})`);

        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;

      } catch (error) {
        console.error('Search background load error:', error);
        throw error;
      }
    }

    this.searchCache.set(searchQuery, allProducts);
    this.searchPartialCache.delete(searchQuery);
    console.log(`Full search cached: ${allProducts.length} products`);
  }

  /**
   * Fetch filtered and sorted search products from cache
   * @param {string} searchQuery - The search query string
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Filtered and sorted products
   */
  async fetchFilteredSearchProducts(searchQuery, filters) {
    let allProducts;

    if (this.searchCache.has(searchQuery)) {
      allProducts = this.searchCache.get(searchQuery);
    } else if (this.searchLoadingPromises.has(searchQuery)) {
      await this.searchLoadingPromises.get(searchQuery);
      allProducts = this.searchCache.get(searchQuery);
    } else {
      // Fallback: use partial cache if available
      const partial = this.searchPartialCache.get(searchQuery);
      allProducts = partial ? partial.products : [];
    }

    if (!allProducts || allProducts.length === 0) return [];

    const filtered = this.applyClientFilters(allProducts, filters);
    const sorted = this.sortProducts(filtered, filters.sort);
    return sorted;
  }

  /**
   * Check if search results are fully loaded
   * @param {string} searchQuery - The search query string
   * @returns {boolean}
   */
  isSearchFullyLoaded(searchQuery) {
    return this.searchCache.has(searchQuery);
  }

  /**
   * Check if search results are currently loading in background
   * @param {string} searchQuery - The search query string
   * @returns {boolean}
   */
  isSearchLoadingInBackground(searchQuery) {
    return this.searchLoadingPromises.has(searchQuery);
  }

  /**
   * Fetch just the product count for a collection (lightweight query for fast pagination)
   * @param {string} collectionHandle - The collection handle
   * @returns {Promise<number|null>} Product count or null if failed
   */
  async fetchCollectionProductCount(collectionHandle) {
    // Note: productsCount is NOT available in Shopify Storefront API (Admin API only)
    // This method is kept for potential future use if Shopify adds this to Storefront API
    const query = `
      query getCollectionCount($handle: String!) {
        collection(handle: $handle) {
          productsCount {
            count
            precision
          }
        }
      }
    `;

    try {
      const data = await this.executeGraphQLQuery(query, { handle: collectionHandle });
      if (data && data.collection && data.collection.productsCount) {
        const count = data.collection.productsCount.count;
        console.log(`Got product count for ${collectionHandle}: ${count} (precision: ${data.collection.productsCount.precision})`);
        return count;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch product count:', error);
      return null;
    }
  }

  /**
   * Execute a GraphQL query against Shopify Storefront API
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {Promise<Object>} Query response data
   */
  async executeGraphQLQuery(query, variables) {
    const endpoint = `https://${window.Shopify.shop}/api/${this.apiVersion}/graphql.json`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({
        query,
        variables
      })
    });

    if (!response.ok) {
      throw new Error(`GraphQL HTTP error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(`GraphQL query failed: ${result.errors[0].message}`);
    }

    return result.data;
  }

  /**
   * Process a GraphQL product node into our standard format
   * @param {Object} node - GraphQL product node
   * @returns {Object} Processed product
   */
  processGraphQLProduct(node) {
    const firstVariant = node.variants.edges[0]?.node || {};
    const firstImage = node.images.edges[0]?.node || null;

    // DEBUG: Log raw GraphQL data for first product
    if (!this._loggedFirstProduct) {
      console.log('Raw GraphQL product data:', {
        title: node.title,
        variant: firstVariant,
        availableForSale: firstVariant.availableForSale
      });
      this._loggedFirstProduct = true;
    }

    // Extract metafields into a flat object
    const metafields = {};
    if (node.metafields) {
      node.metafields.forEach(field => {
        if (field) {
          metafields[field.key] = field.value;
        }
      });
    }

    // Parse metafield values
    // Sold always wins: a zero-inventory instrument is gone, so a leftover hold
    // flag is meaningless. Shopify can report both at once (Access sometimes
    // leaves Action at "Hold" after a sale, and products imported before the
    // April 2026 transform fix were never re-imported), so clamp it here rather
    // than trusting the metafield downstream.
    const available = firstVariant.availableForSale || false;
    const onHold = available && (metafields.onHold === 'true' || metafields.onHold === true);
    const justArrived = metafields.justArrived === 'true' || metafields.justArrived === true;
    // Handle case-insensitive promoHeadline lookup (Shopify may return lowercase)
    const promoHeadline = metafields.promoHeadline || metafields.promoheadline || null;
    // Handle case-insensitive ShowPrice lookup (default to true if not set)
    const showPriceValue = metafields.ShowPrice || metafields.showPrice || metafields.showprice;
    const showPrice = showPriceValue === undefined || showPriceValue === null || showPriceValue === 'true' || showPriceValue === true;
    
    // Use decade metafield if available, otherwise fall back to extracting from title
    let decade = metafields.decade;
    if (!decade && metafields.year) {
      decade = this.convertYearToDecade(parseInt(metafields.year));
    }
    if (!decade) {
      decade = this.extractDecadeFromTitle(node.title);
    }
    
    // DEBUG: Log decade calculation for first few products
    if (!this._decadeLogCount || this._decadeLogCount < 3) {
      this._decadeLogCount = (this._decadeLogCount || 0) + 1;
      console.log(`Decade calculation for "${node.title}":`, {
        metafieldDecade: metafields.decade,
        metafieldYear: metafields.year,
        extractedFromTitle: this.extractDecadeFromTitle(node.title),
        finalDecade: decade,
        allMetafields: metafields
      });
    }

    return {
      id: this.extractNumericId(node.id),
      handle: node.handle,
      title: node.title,
      sku: firstVariant.sku || 'No SKU',
      available: available,
      onHold: onHold,
      price: parseFloat(firstVariant.price?.amount) || 0,
      vendor: node.vendor || '',
      image: firstImage?.url || null,
      tags: node.tags || [],
      decade: decade,
      year: metafields.year || null,
      justArrived: justArrived,
      promoHeadline: promoHeadline,
      showPrice: showPrice,
      _rawProduct: node // Store for reference
    };
  }

  /**
   * Extract numeric ID from GraphQL global ID
   * @param {string} gid - GraphQL global ID (e.g., "gid://shopify/Product/123456")
   * @returns {number} Numeric product ID
   */
  extractNumericId(gid) {
    const parts = gid.split('/');
    return parseInt(parts[parts.length - 1]);
  }

  /**
   * Extract decade from product title (fallback if metafield not available)
   * @param {string} title - Product title
   * @returns {string} Decade string
   */
  extractDecadeFromTitle(title) {
    if (!title) return 'Unknown';
    
    const yearMatch = title.match(/\((\d{4})\)/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      if (year >= 1800 && year <= new Date().getFullYear()) {
        return this.convertYearToDecade(year);
      }
    }
    return 'Unknown';
  }

  /**
   * Convert year to decade category
   * @param {number} year - Year
   * @returns {string} Decade string
   */
  convertYearToDecade(year) {
    if (!year || isNaN(year)) return 'Unknown';
    
    // Match the format used in Shopify metafields (no apostrophes)
    if (year >= 2000) return '2000s';
    if (year >= 1990) return '1990s';
    if (year >= 1980) return '1980s';
    if (year >= 1970) return '1970s';
    if (year >= 1960) return '1960s';
    if (year >= 1950) return '1950s';
    if (year >= 1940) return '1940s';
    if (year >= 1930) return '1930s';
    if (year >= 1920) return '1920s';
    if (year >= 1910) return '1910s';
    return 'pre 1910';
  }

  /**
   * Apply client-side filters to products
   * @param {Array} products - All products
   * @param {Object} filters - Filter options
   * @returns {Array} Filtered products
   */
  applyClientFilters(products, filters) {
    console.log('Filtering products:', {
      totalProducts: products.length,
      filters: filters,
      sampleProduct: products[0] ? {
        id: products[0].id,
        title: products[0].title,
        available: products[0].available,
        onHold: products[0].onHold,
        decade: products[0].decade
      } : null
    });

    let filtered = products;

    // Filter by availability
    // Hide: sold out products. On-hold is NOT an escape hatch — a held item is
    // still in stock, so it passes on `available` alone.
    if (!filters.includeSold) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(product => product.available);
      console.log(`Availability filter: ${beforeFilter} → ${filtered.length} products (available)`);
    }

    // Filter by maker/vendor
    if (filters.maker) {
      if (filters.maker === 'Other') {
        const predefinedMakers = [
          'Fender', 'Martin', 'Gibson', 'D\'Angelico', 'Gilchrist',
          'Collings', 'Rickenbacker', 'Gretsch', 'Epiphone', 'National',
          'Guild', 'Harmony', 'Burns', 'Vox', 'Danelectro', 'Vega'
        ];
        filtered = filtered.filter(product => {
          const isSpecificMaker = predefinedMakers.some(maker => {
            if (product.vendor === maker) return true;
            if (maker === 'Martin' && product.vendor.includes('Martin')) return true;
            if (maker === 'Burns' && product.vendor.includes('Burns')) return true;
            return false;
          });
          return !isSpecificMaker;
        });
      } else {
        filtered = filtered.filter(product => {
          if (filters.maker === 'Martin' && product.vendor.includes('Martin')) return true;
          if (filters.maker === 'Burns' && product.vendor.includes('Burns')) return true;
          return product.vendor === filters.maker;
        });
      }
    }

    // Filter by era/decade (now using metafield directly!)
    if (filters.era) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(product => product.decade === filters.era);
      console.log(`Era filter (${filters.era}): ${beforeFilter} → ${filtered.length} products`);
    }

    console.log(`Final filtered count: ${filtered.length} products`);
    return filtered;
  }

  /**
   * Partition products so on-hold items appear at the end
   * Preserves relative order within each group (stable partition)
   * @param {Array} products - Products array (already sorted)
   * @returns {Array} Products with on-hold items moved to end
   */
  partitionOnHoldToEnd(products) {
    const notOnHold = [];
    const onHold = [];
    for (const product of products) {
      if (product.onHold) {
        onHold.push(product);
      } else {
        notOnHold.push(product);
      }
    }
    return [...notOnHold, ...onHold];
  }

  /**
   * Sort products client-side
   * @param {Array} products - Products to sort
   * @param {string} sortOption - Sort option ('recent', 'price_low', 'price_high', 'maker_az')
   * @returns {Array} Sorted products
   */
  sortProducts(products, sortOption) {
    // Create a copy to avoid mutating cached data
    const sorted = [...products];

    // Apply the requested sort order
    switch(sortOption) {
      case 'recent':
        // Server returns products in CREATED desc order (= most recently modified).
        // No client-side re-sort needed; partitionOnHoldToEnd handles on-hold items.
        break;

      case 'price_low':
        sorted.sort((a, b) => a.price - b.price);
        break;

      case 'price_high':
        sorted.sort((a, b) => b.price - a.price);
        break;

      case 'maker_az':
        sorted.sort((a, b) => (a.vendor || '').localeCompare(b.vendor || ''));
        break;
    }

    // Partition on-hold items to the end (preserves sort order within each group)
    return this.partitionOnHoldToEnd(sorted);
  }

  /**
   * Clear cache (useful for testing)
   */
  clearCache() {
    this.cache.clear();
    this.searchCache.clear();
    this.searchPartialCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      cachedCollections: this.cache.size,
      collections: Array.from(this.cache.keys())
    };
  }
}

// Create global instance
window.GraphQLProductManager = new GraphQLProductManager();

